import React, { useEffect, useMemo, useState, createElement } from 'react';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { ResourceMatrix } from './components/ResourceMatrix';
import { AddProjectDialog } from './components/AddProjectDialog';
import { AddEmployeeDialog } from './components/AddEmployeeDialog';
import { EmployeeDetailDialog } from './components/EmployeeDetailDialog';
import { ProjectDetailDialog } from './components/ProjectDetailDialog';
import { LoginPage } from './components/LoginPage';
import { mitarbeiter as initialMitarbeiter, projekte as initialProjekte, zuweisungen as initialZuweisungen, abwesenheiten as initialAbwesenheiten } from './data/mockData';
import { Abteilung, Projekt, Mitarbeiter, ProjektStatus } from './types';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar state
  const [mitarbeiter, setMitarbeiter] = useState(initialMitarbeiter);
  const [projekte, setProjekte] = useState(initialProjekte);
  const [zuweisungen, setZuweisungen] = useState(initialZuweisungen);
  const [abwesenheiten, setAbwesenheiten] = useState(initialAbwesenheiten);
  const [selectedAbteilungen, setSelectedAbteilungen] = useState<Abteilung[]>(['Konstruktion', 'E-Konstruktion', 'Montage', 'Einkauf', 'Inbetriebnahme']);
  const [selectedProjektId, setSelectedProjektId] = useState<string | undefined>(undefined);
  // KW Navigation
  const [currentKw, setCurrentKw] = useState(7);
  // Dark Mode
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('darkMode');
      if (stored !== null) return stored === 'true';
    }
    return false;
  });
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('darkMode', String(isDark));
  }, [isDark]);
  const toggleDarkMode = () => setIsDark(prev => !prev);
  // Dialog States
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Mitarbeiter | null>(null);
  const [selectedProject, setSelectedProject] = useState<Projekt | null>(null);
  // Handlers
  const handleLogin = (email: string) => {
    setIsAuthenticated(true);
  };
  const handleToggleAbteilung = (abt: Abteilung) => {
    setSelectedAbteilungen(prev => prev.includes(abt) ? prev.filter(a => a !== abt) : [...prev, abt]);
  };
  const handleAddProject = (newProject: Omit<Projekt, 'id'>) => {
    const id = `p${Date.now()}`;
    setProjekte([...projekte, {
      ...newProject,
      id
    }]);
  };
  const handleAddEmployee = (newEmployee: Omit<Mitarbeiter, 'id'>) => {
    const id = `m${Date.now()}`;
    setMitarbeiter([...mitarbeiter, {
      ...newEmployee,
      id
    }]);
  };
  const handleStatusChange = (projektId: string, status: ProjektStatus) => {
    setProjekte(prev => prev.map(p => p.id === projektId ? {
      ...p,
      status
    } : p));
  };
  const handleExportCSV = () => {
    const headers = ['Mitarbeiter', 'Abteilung', 'Projekt', 'Phase', 'Start KW', 'Ende KW', 'Auslastung %'];
    const rows = zuweisungen.map(z => {
      const m = mitarbeiter.find(m => m.id === z.mitarbeiterId);
      const p = projekte.find(p => p.id === z.projektId);
      return [m?.name || '', m?.abteilung || '', p?.name || '', z.phase, z.startKw, z.endKw, z.auslastungProzent].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ressourcen_export_kw${currentKw}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleKwChange = (kw: number) => {
    setCurrentKw(Math.max(1, Math.min(52, kw)));
  };
  // Stats Calculation
  const stats = useMemo(() => {
    let conflicts = 0;
    const weeks = Array.from({
      length: 26
    }, (_, i) => i + 1);
    mitarbeiter.forEach(m => {
      const employeeAssignments = zuweisungen.filter(z => z.mitarbeiterId === m.id);
      let hasConflict = false;
      weeks.forEach(kw => {
        const load = employeeAssignments.filter(z => kw >= z.startKw && kw < z.endKw).reduce((sum, z) => sum + z.auslastungProzent, 0);
        if (load > 100) hasConflict = true;
      });
      if (hasConflict) conflicts++;
    });
    return {
      mitarbeiterCount: mitarbeiter.length,
      projektCount: projekte.filter(p => p.status === 'Aktiv').length,
      konflikteCount: conflicts
    };
  }, [mitarbeiter, zuweisungen, projekte]);
  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} data-id="element-0" />;
  }
  return <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden font-sans selection:bg-primary/20 transition-colors duration-300" data-id="element-1">
      <Header currentKw={currentKw} onAddProjekt={() => setIsProjectDialogOpen(true)} onAddMitarbeiter={() => setIsEmployeeDialogOpen(true)} onExport={handleExportCSV} onKwChange={handleKwChange} stats={stats} mitarbeiter={mitarbeiter} projekte={projekte} onEmployeeClick={setSelectedEmployee} onProjectClick={setSelectedProject} isDark={isDark} onToggleDark={toggleDarkMode} onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} data-id="element-2" />

      <div className="flex flex-1 overflow-hidden relative" data-id="element-3">
        <Sidebar allAbteilungen={['Konstruktion', 'E-Konstruktion', 'Montage', 'Einkauf', 'Inbetriebnahme']} selectedAbteilungen={selectedAbteilungen} onToggleAbteilung={handleToggleAbteilung} projekte={projekte} selectedProjektId={selectedProjektId} onSelectProjekt={setSelectedProjektId} isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} data-id="element-4" />

        <main className="flex-1 p-2 sm:p-4 overflow-hidden flex flex-col relative w-full" data-id="element-5">
          <div className="absolute inset-0 bg-gradient-to-br from-background to-muted/20 -z-10" data-id="element-6" />
          <ResourceMatrix mitarbeiter={mitarbeiter} projekte={projekte} zuweisungen={zuweisungen} abwesenheiten={abwesenheiten} startKw={1} endKw={26} selectedAbteilungen={selectedAbteilungen} filterProjektId={selectedProjektId} currentKw={currentKw} onEmployeeClick={setSelectedEmployee} onProjectClick={setSelectedProject} onKwClick={handleKwChange} data-id="element-7" />
        </main>
      </div>

      <AddProjectDialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen} onAdd={handleAddProject} data-id="element-8" />

      <AddEmployeeDialog open={isEmployeeDialogOpen} onOpenChange={setIsEmployeeDialogOpen} onAdd={handleAddEmployee} data-id="element-9" />

      <EmployeeDetailDialog open={!!selectedEmployee} onOpenChange={open => !open && setSelectedEmployee(null)} employee={selectedEmployee} zuweisungen={zuweisungen} projekte={projekte} abwesenheiten={abwesenheiten} data-id="element-10" />

      <ProjectDetailDialog open={!!selectedProject} onOpenChange={open => !open && setSelectedProject(null)} projekt={selectedProject} zuweisungen={zuweisungen} mitarbeiter={mitarbeiter} onStatusChange={handleStatusChange} data-id="element-11" />
    </div>;
}